// ============================================================================
// Bid Walk-through — a scripted, self-playing demo that builds a full bid live.
// ----------------------------------------------------------------------------
// Click "Play walk-through" and this rebuilds the current bid in front of the
// user: it types the cover details, sets the margin, then adds every phase with
// its equipment/labour rows one at a time — while a narration bar explains each
// step. It ends on the dashboard so the finished, in-depth bid is on screen.
//
// The engine is UI-agnostic: it only calls back into the page (commitWorkbook,
// setCover, focus*), so all the real editing UI updates exactly as if a person
// were filling it out.
// ============================================================================

import { useCallback, useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlay, faStop, faRotateRight, faXmark, faCheck } from '@fortawesome/free-solid-svg-icons';
import type { Client } from '@frontend/Models/Client/ClientModels';
import ClientService from '@frontend/AppSettings/services/ClientService';
import type { CatalogOption } from '../Admin/useCatalogOptions';
import { loadRateCardOptions } from '../Admin/useRateCardOptions';
import { formatBidMoney, getBidUuid, type Bid, type BidStatus } from '../bidStore';
import { saveMobLoads, type MobLoadRow } from '../bidService';
import { companyOf } from '../clientContacts';
import {
  MOB_LINE_PREFIX,
  buildWorkbookForScope,
  workbookTotals,
  type BidPhase,
  type BidWorkbook,
} from './bidWorkbook';
import './BidWalkthrough.css';

// ---------------------------------------------------------------------------
// The scenario the walk-through fills in. A realistic, fully-scoped pipeline
// bid so the finished result looks like a genuine, in-depth estimate.
// ---------------------------------------------------------------------------
export const SCENARIO = {
  name: 'Sturgeon Lake 12" Sales Line',
  client: 'Keyera',
  area: 'Sturgeon Lake, AB',
  estimator: 'Jordan Avery',
  scope: '9.2 km of 12" sales line',
  description:
    'Fixed-price install across mixed muskeg and cleared ROW. Three-crew spread on '
    + '24hr access. Hydro test with 50/50 water/methanol; winter conditions assumed.',
  status: 'In Review' as BidStatus,
  lengthM: 9200,
  /** Target profit margin (profit / bid total). The markup % is derived from this. */
  targetMargin: 0.2,
};

// Markup % applied on base cost to hit the target margin: markup = m / (1 - m).
const derivedProfitPct = Math.round((SCENARIO.targetMargin / (1 - SCENARIO.targetMargin)) * 100);

// Playback timing (ms). Tuned so it reads as "being filled out" but stays brisk.
const TYPE = 26; // per character while typing text fields
const FIELD = 240; // pause between cover fields
const BEAT = 1500; // longer pause on the summary / closing beats

// The first couple of phases play slowly so you can follow each row being added;
// the rest are "speed-run" so the full estimate still finishes quickly.
const SLOW_PHASES = 2;
const PHASE_SLOW = 1150; // pause after a phase header appears (slow phases)
const ROW_SLOW = 340; // pause between line-item rows (slow phases)
const PHASE_FAST = 240; // pause after a phase header appears (fast phases)
const ROW_FAST = 32; // pause between line-item rows (fast phases)

export type WalkPhase = 'idle' | 'running' | 'done';

export interface WalkState {
  phase: WalkPhase;
  caption: string;
  progress: number; // 0..1
  total: string; // formatted final bid total, once known
}

export interface WalkthroughDeps {
  bidId: string;
  /** Persist a full workbook (updates the live editor + the store). */
  commitWorkbook: (wb: BidWorkbook) => void;
  /** Patch the bid's cover/header fields (client, name, status, …). */
  setCover: (patch: Partial<Bid>) => void;
  /** Bring the Bid Details tab + Summary sheet into view. */
  focusSummary: () => void;
  /** Bring the Bid Details tab + a specific phase into view. */
  focusPhase: (phaseId: string) => void;
  /** Bring the Dashboard tab into view. */
  focusDashboard: () => void;
}

const IDLE: WalkState = { phase: 'idle', caption: '', progress: 0, total: '' };

/**
 * The walkthrough's client pick (Build G): the cover's client is a CONTACT PICKER now, so the
 * demo SELECTS a real contact the way an estimator would — preferring a company matching
 * /cenovus/i (the requested demo client), else the first contact with a usable label. Null on
 * an empty tenant, where the engine falls back to typing the scenario company as before.
 */
export function pickWalkthroughContact(clients: Client[]): Client | null {
  const cenovus = clients.find((c) => /cenovus/i.test((c.Company ?? '').trim()));
  if (cenovus) return cenovus;
  // COMPANY-bearing contacts only (plan review M1 + diff review L3): the pick denormalises the
  // contact's company verbatim onto the cover, so a person-only or blank row would stamp an
  // EMPTY Client Company on the demo. No company anywhere → null, and the engine types the
  // scenario company instead — the demo never picks a nameless client.
  return clients.find((c) => (c.Company ?? '').trim() !== '') ?? null;
}

/**
 * The demo load matrix (Build G): the seeded Mobilization/Demobilization pair's own legal-loads
 * total, split across the customer sheet's equipment types with the sheet's own proportions
 * (6 excavators : 2 dozers : 5 pipelayers : 3 sloops : 1 warehousing : 1 lay down) — a
 * remainder loop keeps the sum EXACT, and zero rows are dropped rather than seeded as clutter.
 */
export function splitLoads(total: number): MobLoadRow[] {
  if (!Number.isFinite(total) || total <= 0) return [];
  const types: [string, number][] = [
    ['Excavators', 6], ['Dozers', 2], ['Pipelayers', 5],
    ['Sloops', 3], ['Warehousing', 1], ['Lay down', 1],
  ];
  const weightSum = types.reduce((n, [, w]) => n + w, 0);
  const rows = types.map(([label, w]) => ({
    label, heavyLoads: 0, legalLoads: Math.floor((total * w) / weightSum), pickupLoads: 0,
  }));
  let remainder = total - rows.reduce((n, r) => n + r.legalLoads, 0);
  for (let i = 0; remainder > 0; i = (i + 1) % rows.length) {
    rows[i].legalLoads += 1;
    remainder -= 1;
  }
  return rows.filter((r) => r.legalLoads > 0);
}

const norm = (s: string): string => s.toLowerCase().trim().replace(/\s+/g, ' ');

/**
 * Seed shorthand → the catalogs' canonical names (plan walkthrough-catalog-linking). The
 * STD_PHASES descriptions must stay as they are (the reference generator derives
 * phase-templates.json from them), so the DEMO maps them to the customer's own spellings at
 * run time. Only names verified against weaver-reference/equipment.json + labour.json belong
 * here — an alias that misses simply leaves the line custom.
 */
export const WALKTHROUGH_CATALOG_ALIASES: Record<string, string> = {
  // equipment
  '350 hoe': 'Trackhoe 350 / 345 / 330 or Equivalent',
  '350 hoes': 'Trackhoe 350 / 345 / 330 or Equivalent',
  '350/330 hoe': 'Trackhoe 350 / 345 / 330 or Equivalent',
  'd6 dozer': 'Dozer D6T LGP 6 Way Blade',
  'd6t dozer': 'Dozer D6T LGP 6 Way Blade',
  '561 boom': 'Pipelayer — 561 Hydraulic',
  '572 boom': 'Pipelayer — 572 Hydraulic',
  '325 cfm compressor': 'Air Compressor — 325 CFM',
  'sandblast unit': 'Deck Truck w/ Sandblasting Equipment (sand not included)',
  // labour
  operator: 'Equipment Operator',
  operators: 'Equipment Operator',
  'intermediate operators': 'Equipment Operator',
  labourers: 'Labourer',
  superintendent: 'Field Construction Superintendent',
};

/**
 * Link the built demo workbook's lines to the bid's frozen catalogs — the dropdown pick's own
 * write, applied in bulk: id + canonical name + catalog rate (a rateless option links the
 * identity but keeps the estimate, `pickCatalogItem`'s rule). DELIBERATELY untouched:
 * `source: 'sub'` lines (sub pricing comes from quotes, never the in-house catalog), the flat
 * day-rate trucks and specialty trades with no catalog row — the demo's "couple of
 * hand-entered lines" are real, not an accident. Empty option lists (blind caller, no card)
 * return the workbook unchanged.
 */
export function linkLinesToCatalog(
  wb: BidWorkbook,
  equipment: CatalogOption[],
  labour: CatalogOption[],
): BidWorkbook {
  if (equipment.length === 0 && labour.length === 0) return wb;
  const byLabel = (opts: CatalogOption[]): Map<string, CatalogOption> => {
    const m = new Map<string, CatalogOption>();
    opts.forEach((o) => {
      const key = norm(o.label);
      if (key && !m.has(key)) m.set(key, o);
    });
    return m;
  };
  const eqByLabel = byLabel(equipment);
  const labByLabel = byLabel(labour);
  return {
    ...wb,
    phases: wb.phases.map((p) => ({
      ...p,
      items: p.items.map((it) => {
        if (it.source === 'sub') return it;
        const wanted = WALKTHROUGH_CATALOG_ALIASES[norm(it.description)] ?? it.description;
        const option = (it.kind === 'labour' ? labByLabel : eqByLabel).get(norm(wanted));
        if (!option) return it;
        return {
          ...it,
          ...(it.kind === 'labour'
            ? { labourRoleId: option.id }
            : { equipmentItemId: option.id }),
          description: option.label,
          rate: option.rate ?? it.rate,
        };
      }),
    })),
  };
}

const addDays = (iso: string, days: number): string => {
  const d = iso ? new Date(iso) : new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().split('T')[0];
};

export function useBidWalkthrough(deps: WalkthroughDeps): {
  state: WalkState;
  start: () => void;
  stop: () => void;
} {
  const [state, setState] = useState<WalkState>(IDLE);
  const abort = useRef(false);
  const running = useRef(false);
  // Per-run generation (diff review L1): stop-then-replay resets `abort`, and an OLD run still
  // parked in an await (a slow contacts fetch, a long beat) would otherwise wake up and
  // interleave with the new one — dueling covers, double PUTs. A run only proceeds while it is
  // still the CURRENT generation.
  const generation = useRef(0);
  // Keep the latest deps without re-triggering the runner.
  const depsRef = useRef(deps);
  depsRef.current = deps;

  const stop = useCallback(() => {
    generation.current += 1;
    abort.current = true;
    running.current = false;
    setState(IDLE);
  }, []);

  // Abort if the component unmounts mid-play.
  useEffect(() => () => { abort.current = true; }, []);

  const start = useCallback(() => {
    if (running.current) return;
    running.current = true;
    abort.current = false;

    const d = depsRef.current;
    const myGeneration = generation.current + 1;
    generation.current = myGeneration;
    const sleep = (ms: number): Promise<void> => new Promise((res) => setTimeout(res, ms));
    const stopped = (): boolean => abort.current || generation.current !== myGeneration;

    // Build the target estimate once so progress + totals are known up front.
    const target = buildWorkbookForScope(SCENARIO.lengthM, {
      profitPct: derivedProfitPct,
      crewScale: 1,
    });
    const totalUnits =
      8 + target.phases.reduce((n, p) => n + 1 + p.items.length, 0);
    let done = 0;

    const update = (caption: string, add = 0): void => {
      done += add;
      setState((s) => ({
        ...s,
        phase: 'running',
        caption,
        progress: Math.min(0.99, done / totalUnits),
      }));
    };

    const typeInto = async (field: keyof Bid, full: string): Promise<void> => {
      let s = '';
      for (const ch of full) {
        if (stopped()) return;
        s += ch;
        d.setCover({ [field]: s } as Partial<Bid>);
        await sleep(TYPE);
      }
    };

    // Working copy of the workbook we grow, phase by phase.
    let wb: BidWorkbook = { rowLengthM: 0, profitPct: 0, phases: [] };
    const commit = (): void => d.commitWorkbook(wb);

    const revealPhase = async (tp: BidPhase, slow: boolean): Promise<void> => {
      const phasePause = slow ? PHASE_SLOW : PHASE_FAST;
      const rowPause = slow ? ROW_SLOW : ROW_FAST;
      // Phase header first (empty), so its tables visibly fill row by row.
      let phase: BidPhase = { ...tp, items: [] };
      wb = { ...wb, phases: [...wb.phases, phase] };
      commit();
      d.focusPhase(phase.id);
      update(/mob/i.test(tp.name)
        ? `Adding phase “${tp.name}” — its load matrix prices the Mobilization / Demobilization pair…`
        : `Adding phase “${tp.name}” — distance & production rate…`, 1);
      await sleep(phasePause);

      for (const it of tp.items) {
        if (stopped()) return;
        phase = { ...phase, items: [...phase.items, it] };
        wb = { ...wb, phases: wb.phases.map((p) => (p.id === phase.id ? phase : p)) };
        commit();
        const label = it.kind === 'labour' ? 'Labour' : 'Equipment';
        const sub = it.source === 'sub' ? ' (subcontract)' : '';
        const picked = it.equipmentItemId || it.labourRoleId ? ' — picked from the catalog' : '';
        update(`   ↳ ${label}: ${it.description}${sub}${picked}`, 1);
        await sleep(rowPause);
      }
    };

    const run = async (): Promise<void> => {
      // 1. Blank slate.
      d.focusSummary();
      d.setCover({
        name: '',
        client: '',
        clientContactId: '',
        area: '',
        estimator: '',
        scope: '',
        description: '',
        status: 'Draft',
      });
      wb = { rowLengthM: 0, profitPct: 0, phases: [] };
      commit();
      update('Starting from a blank draft…');
      await sleep(BEAT);
      if (stopped()) return;

      // 2. Name the bid (top-bar title).
      update('Naming the bid…');
      await typeInto('name', SCENARIO.name);
      update('', 1);
      await sleep(FIELD);
      if (stopped()) return;

      // 3. Client + location. The cover's client became a CONTACT PICKER (Build G): select a
      // real contact through the picker's own patch shape, or fall back to typing the scenario
      // company on a tenant with no contacts — the demo never breaks.
      update('Selecting the client contact…');
      let contacts: Client[] = [];
      try {
        contacts = await ClientService.getClients();
      } catch {
        contacts = [];
      }
      if (stopped()) return;
      const contact = pickWalkthroughContact(contacts);
      if (contact) {
        d.setCover({
          clientContactId: contact.ClientID,
          client: companyOf(contacts, contact.ClientID),
        });
        await sleep(FIELD * 2);
      } else {
        await typeInto('client', SCENARIO.client);
        await sleep(FIELD);
      }
      await typeInto('area', SCENARIO.area);
      update('', 1);
      await sleep(FIELD);
      if (stopped()) return;

      // 4. Estimator, status, dates.
      update('Estimator, status and key dates…');
      await typeInto('estimator', SCENARIO.estimator);
      d.setCover({ status: SCENARIO.status });
      const created = new Date().toISOString().split('T')[0];
      d.setCover({ createdDate: created, dueDate: addDays(created, 35) });
      update('', 1);
      await sleep(FIELD);
      if (stopped()) return;

      // 5. Scope + assumptions.
      update('Scope and key assumptions…');
      await typeInto('scope', SCENARIO.scope);
      await sleep(FIELD);
      await typeInto('description', SCENARIO.description);
      update('', 2);
      await sleep(FIELD);
      if (stopped()) return;

      // 6. ROW length (profit is calculated automatically at the end).
      update('Setting the right-of-way length…', 1);
      wb = { ...wb, rowLengthM: target.rowLengthM };
      commit();
      await sleep(BEAT * 0.6);
      if (stopped()) return;

      // 6.4. Link the crews to the company catalogs (plan walkthrough-catalog-linking): every
      // matched line carries the dropdown pick's own write — id, canonical name, catalog rate —
      // and the sub lines + day-rate trucks stay hand-entered on purpose. Line COUNTS are
      // unchanged, so the progress accounting over `target` stays exact.
      update('Linking crews to the equipment & labour catalogs…');
      const [eqOptions, labOptions] = await Promise.all([
        loadRateCardOptions(d.bidId, 'equipment'),
        loadRateCardOptions(d.bidId, 'labour'),
      ]);
      if (stopped()) return;
      const linked = linkLinesToCatalog(target, eqOptions, labOptions);
      await sleep(BEAT * 0.5);
      if (stopped()) return;

      // 6.5. Seed the mob/demob load matrix (Build M's table) with the counts the seeded
      // Mobilization/Demobilization pair prices — so the phase opens with the matrix populated.
      // Replace-all (a replay overwrites, never doubles); silent on failure — the show goes on.
      const mobPhase = linked.phases.find((p) => /mob/i.test(p.name));
      const mobQty = mobPhase?.items
        .find((it) => it.description.startsWith(MOB_LINE_PREFIX))?.qty ?? 0;
      const uuid = getBidUuid(d.bidId);
      if (uuid && mobQty > 0) {
        try {
          await saveMobLoads(uuid, splitLoads(mobQty));
        } catch { /* advisory seeding only — a viewer without bid rights just skips it */ }
      }
      if (stopped()) return;

      // 7. Build every phase, row by row. First couple slow, then speed-run.
      for (let i = 0; i < linked.phases.length; i += 1) {
        if (stopped()) return;
        await revealPhase(linked.phases[i], i < SLOW_PHASES);
      }

      // 8. Roll-up summary — base cost is now known.
      d.focusSummary();
      update('Every phase rolls up into the estimate summary.');
      await sleep(BEAT);
      if (stopped()) return;

      // 9. Auto-calculate profit to hit the target margin, applied at the end.
      const marginPct = Math.round(SCENARIO.targetMargin * 100);
      update(
        `Calculating profit for a ${marginPct}% target margin → ${derivedProfitPct}% markup…`,
        1,
      );
      wb = { ...wb, profitPct: derivedProfitPct };
      d.setCover({ targetMargin: SCENARIO.targetMargin });
      commit();
      await sleep(BEAT);
      if (stopped()) return;

      // 10. Finish on the dashboard.
      d.focusDashboard();
      update('The dashboard visualizes the finished bid.');
      await sleep(BEAT);
      if (stopped()) return;

      // 11. Done.
      const total = formatBidMoney(Math.round(workbookTotals(wb).bidTotal));
      running.current = false;
      setState({
        phase: 'done',
        caption: 'Walk-through complete — a full bid, built end to end.',
        progress: 1,
        total,
      });
    };

    void run().catch(() => {
      running.current = false;
      setState(IDLE);
    });
  }, []);

  return { state, start, stop };
}

// ===========================================================================
// Narration overlay — the caption bar shown while (and after) the demo plays.
// ===========================================================================
interface OverlayProps {
  state: WalkState;
  onStop: () => void;
  onReplay: () => void;
  onClose: () => void;
}

export const BidWalkthroughOverlay = ({ state, onStop, onReplay, onClose }: OverlayProps): JSX.Element | null => {
  if (state.phase === 'idle') return null;
  const isDone = state.phase === 'done';

  return (
    <div className="bid-walk-overlay" role="status" aria-live="polite">
      <div className={`bid-walk-card${isDone ? ' is-done' : ''}`}>
        <div className="bid-walk-icon">
          <FontAwesomeIcon icon={isDone ? faCheck : faPlay} />
        </div>

        <div className="bid-walk-body">
          <div className="bid-walk-head">
            <span className="bid-walk-title">
              {isDone ? 'Walk-through complete' : 'Building bid…'}
            </span>
            {isDone && state.total && (
              <span className="bid-walk-total">Total bid {state.total}</span>
            )}
          </div>
          <p className="bid-walk-caption">{state.caption}</p>
          <div className="bid-walk-progress">
            <div
              className="bid-walk-progress-fill"
              style={{ width: `${Math.round(state.progress * 100)}%` }}
            />
          </div>
        </div>

        <div className="bid-walk-actions">
          {isDone ? (
            <>
              <button className="bid-walk-btn bid-walk-btn-ghost" onClick={onReplay}>
                <FontAwesomeIcon icon={faRotateRight} /> Replay
              </button>
              <button className="bid-walk-btn bid-walk-btn-primary" onClick={onClose}>
                <FontAwesomeIcon icon={faXmark} /> Close
              </button>
            </>
          ) : (
            <button className="bid-walk-btn bid-walk-btn-stop" onClick={onStop}>
              <FontAwesomeIcon icon={faStop} /> Stop
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
